import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import Card from '../../../shared/components/Card';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { getApiBaseUrl } from '../../../core/config/api';
import configService from '../../../shared/services/configService';
import { PersonnelConfig, VehiculeConfig, Service, FonctionArmement, Competence, CompetenceFonctionArmement } from '../../../shared/types/config';
import { useConfigSocket } from '../../../shared/hooks/useConfigSocket';
import PersonnelModal from './components/config/modals/PersonnelModal';
import VehiculeModal from './components/config/modals/VehiculeModal';
import CompetenceModal from './components/config/modals/CompetenceModal';
import FonctionArmementModal from './components/config/modals/FonctionArmementModal';
import PersonnelTab from './components/config/tabs/PersonnelTab';
import VehiculesTab from './components/config/tabs/VehiculesTab';
import CompetencesTab from './components/config/tabs/CompetencesTab';
import FonctionsArmementTab from './components/config/tabs/FonctionsArmementTab';
import BuildingAppearanceFields from './components/config/BuildingAppearanceFields';

interface SystemConfigurationProps {
  embedded?: boolean;
}

const SystemConfiguration: React.FC<SystemConfigurationProps> = ({ embedded = false }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const { t } = usePageTranslation('panel', 'SystemConfiguration');

  // Vérifier l'accès autorisé (uniquement CM - rôle 1)
  const hasAccess = currentUser?.roles_id === 1;
  
  const [activeTab, setActiveTab] = useState<'personnel' | 'vehicules' | 'competences' | 'fonctions-armement' | 'batiments' | 'partenaires' | 'categories-mission' | 'categories-spe'>('personnel');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // États pour les personnels
  const [personnelConfigs, setPersonnelConfigs] = useState<PersonnelConfig[]>([]);
  const [searchPersonnel, setSearchPersonnel] = useState('');
  /** Filtre d’affichage : un service à la fois ou tous (groupés par service) */
  const [personnelFilterService, setPersonnelFilterService] = useState<number | 'all'>('all');
  const [showPersonnelModal, setShowPersonnelModal] = useState(false);
  const [editingPersonnel, setEditingPersonnel] = useState<PersonnelConfig | null>(null);
  const [personnelForm, setPersonnelForm] = useState<Omit<PersonnelConfig, 'id'>>({
    grade: '',
    prix: 0,
    id_competences: [],
    id_service: 0,
    ordre: 0,
  });

  // États pour les véhicules
  const [vehiculeConfigs, setVehiculeConfigs] = useState<VehiculeConfig[]>([]);
  const [searchVehicule, setSearchVehicule] = useState('');
  const [showVehiculeModal, setShowVehiculeModal] = useState(false);
  const [editingVehicule, setEditingVehicule] = useState<VehiculeConfig | null>(null);
  const [vehiculeForm, setVehiculeForm] = useState<Omit<VehiculeConfig, 'id'>>({
    nom: '',
    niveaux: 1,
    prix: 0,
    specialiste: false,
    id_competences_armement: [],
    id_grades_priorite: [],
    id_grades_armement: [],
    kilometrage: 0,
    type: '',
    fonction_depart: '',
    capacite_personnels_min: 0,
    capacite_personnels_max: 0,
    capable_remorque: false,
    capable_lot: false,
    capable_cellule: false,
    capacite_eau: 0,
    capacite_emulseur: 0,
    id_service: 0,
    primary_armament_function_id: null,
    secondary_armament_function_ids: [],
    id_fonctions_armement: [],
    capacite_victimes_ur: 0,
    capacite_victimes_ua: 0,
    capacite_victimes_impliquees: 0,
    capacite_hostiles: 0,
    capacite_vehicules_accidentes: 0,
    capacite_animaux: 0,
    capable_escorte_medicale: false,
    capable_escorte_fdo: false,
  });

  // États pour les services et fonctions armement
  const [services, setServices] = useState<Service[]>([]);
  const [fonctionsArmement, setFonctionsArmement] = useState<FonctionArmement[]>([]);
  const [searchFonctionsArmement, setSearchFonctionsArmement] = useState('');
  const [showFonctionArmementModal, setShowFonctionArmementModal] = useState(false);
  const [editingFonctionArmement, setEditingFonctionArmement] = useState<FonctionArmement | null>(null);
  const [fonctionArmementForm, setFonctionArmementForm] = useState<Omit<FonctionArmement, 'id'>>({
    nom: '',
    description: '',
    id_competences: [],
  });

  // États pour les compétences
  const [competences, setCompetences] = useState<Competence[]>([]);
  const [searchCompetence, setSearchCompetence] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'normal' | 'specialite'>('all');
  const [filterService, setFilterService] = useState<number | 'all'>('all');
  const [showCompetenceModal, setShowCompetenceModal] = useState(false);
  const [editingCompetence, setEditingCompetence] = useState<Competence | null>(null);
  const [competenceForm, setCompetenceForm] = useState<Omit<Competence, 'id'>>({
    nom: '',
    is_specialite: false,
    id_service: null,
    description: '',
    ordre: undefined,
  });

  // États pour catégories de mission / catégories de spécialité
  const [categoriesMission, setCategoriesMission] = useState<{ id: number; nom: string; created_at?: string }[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  interface CategorieSpecialiteItem { id: number; nom: string; id_service: number; ordre: number; actif: number; service_nom?: string }
  const [categoriesSpecialite, setCategoriesSpecialite] = useState<CategorieSpecialiteItem[]>([]);
  const [newCategorieSpeNom, setNewCategorieSpeNom] = useState('');
  const [newCategorieSpeServiceId, setNewCategorieSpeServiceId] = useState<number>(0);
  const [creatingCategorieSpe, setCreatingCategorieSpe] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [editingMissionCategoryId, setEditingMissionCategoryId] = useState<number | null>(null);

  // États pour types de bâtiments (config, menu construction carte)
  interface BuildingTypeConfig {
    id: number;
    code: string;
    service_id: number;
    nom: string;
    icon: string | null;
    couleur: string | null;
    ordre: number;
    prix: number;
    actif: number;
    created_at?: string;
  }
  const [buildingTypesConfig, setBuildingTypesConfig] = useState<BuildingTypeConfig[]>([]);
  const [buildingForm, setBuildingForm] = useState({
    code: '',
    nom: '',
    icon: '',
    couleur: 'text-gray-400',
    ordre: 0,
    actif: 1,
    service_id: 1,
    prix: 0,
  });
  const [editingBuildingId, setEditingBuildingId] = useState<number | null>(null);
  const [savingBuilding, setSavingBuilding] = useState(false);

  // États pour Partenaires (page d'accueil)
  interface PartenaireConfig {
    id: number;
    code: string;
    nom: string;
    description: string | null;
    discord_link: string;
    color: string;
    icon: string | null;
    ordre: number;
    actif: number;
    created_at?: string;
  }
  const PARTENAIRE_COLORS = ['blue', 'red', 'green', 'purple', 'yellow', 'orange'] as const;
  const [partenairesConfig, setPartenairesConfig] = useState<PartenaireConfig[]>([]);
  const [partnerForm, setPartnerForm] = useState({
    code: '',
    nom: '',
    description: '',
    discord_link: '',
    color: 'blue' as typeof PARTENAIRE_COLORS[number],
    icon: '',
    ordre: 0,
    actif: 1,
  });
  const [editingPartnerId, setEditingPartnerId] = useState<number | null>(null);
  const [savingPartner, setSavingPartner] = useState(false);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'personnel') {
        const [personnelRes, servicesRes, competencesRes] = await Promise.all([
          configService.getPersonnelConfigs(),
          configService.getServices(),
          configService.getCompetences(),
        ]);
        if (personnelRes.success && personnelRes.data) {
          setPersonnelConfigs(personnelRes.data);
        } else {
          setError(personnelRes.error || 'Erreur lors du chargement des personnels');
        }
        if (servicesRes.success && servicesRes.data) {
          setServices(servicesRes.data);
        }
        if (competencesRes.success && competencesRes.data) {
          setCompetences(competencesRes.data);
        }
      } else if (activeTab === 'vehicules') {
        const [vehiculeRes, servicesRes, personnelRes, competencesRes, fonctionsRes] = await Promise.all([
          configService.getVehiculeConfigs(),
          configService.getServices(),
          configService.getPersonnelConfigs(),
          configService.getCompetences(),
          configService.getFonctionsArmement(),
        ]);
        if (vehiculeRes.success && vehiculeRes.data) {
          setVehiculeConfigs(vehiculeRes.data);
        } else {
          setError(vehiculeRes.error || 'Erreur lors du chargement des véhicules');
        }
        if (servicesRes.success && servicesRes.data) {
          setServices(servicesRes.data);
        }
        if (personnelRes.success && personnelRes.data) {
          setPersonnelConfigs(personnelRes.data);
        }
        if (competencesRes.success && competencesRes.data) {
          setCompetences(competencesRes.data);
        }
        if (fonctionsRes.success && fonctionsRes.data) {
          setFonctionsArmement(fonctionsRes.data);
        }
      } else if (activeTab === 'competences') {
        const [competencesRes, servicesRes] = await Promise.all([
          configService.getCompetences(),
          configService.getServices(),
        ]);
        if (competencesRes.success && competencesRes.data) {
          setCompetences(competencesRes.data);
        } else {
          setError(competencesRes.error || 'Erreur lors du chargement des compétences');
        }
        if (servicesRes.success && servicesRes.data) {
          setServices(servicesRes.data);
        }
      } else if (activeTab === 'fonctions-armement') {
        const [fonctionsRes, competencesRes] = await Promise.all([
          configService.getFonctionsArmement(),
          configService.getCompetences(),
        ]);
        if (fonctionsRes.success && fonctionsRes.data) {
          setFonctionsArmement(fonctionsRes.data);
        } else {
          setError(fonctionsRes.error || 'Erreur lors du chargement des fonctions d\'armement');
        }
        if (competencesRes.success && competencesRes.data) {
          setCompetences(competencesRes.data);
        }
      } else if (activeTab === 'batiments') {
        const token = localStorage.getItem('token');
        const apiUrl = getApiBaseUrl();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${apiUrl}/api/buildings/config`, { headers, credentials: 'include' });
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) setBuildingTypesConfig(data.data);
      } else if (activeTab === 'partenaires') {
        const token = localStorage.getItem('token');
        const apiUrl = getApiBaseUrl();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${apiUrl}/api/config/partenaires`, { headers, credentials: 'include' });
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) setPartenairesConfig(data.data);
      } else if (activeTab === 'categories-mission' || activeTab === 'categories-spe') {
        const token = localStorage.getItem('token');
        const apiUrl = getApiBaseUrl();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        if (activeTab === 'categories-mission') {
          const res = await fetch(`${apiUrl}/api/cta/categories-mission`, { headers, credentials: 'include' });
          const data = await res.json();
          if (data.success && Array.isArray(data.data)) setCategoriesMission(data.data);
        } else {
          const [catSpeRes, servicesRes] = await Promise.all([
            configService.getCategoriesSpecialite(),
            configService.getServices(),
          ]);
          if (catSpeRes.success && Array.isArray(catSpeRes.data)) setCategoriesSpecialite(catSpeRes.data);
          if (servicesRes.success && servicesRes.data) setServices(servicesRes.data);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Charger les données
  useEffect(() => {
    if (hasAccess) {
      loadAllData();
    }
  }, [hasAccess, activeTab, loadAllData]);

  // Connexion Socket.io pour les mises à jour en temps réel
  useConfigSocket({
    onPersonnelCreated: (data) => {
      if (activeTab === 'personnel') {
        setPersonnelConfigs(prev => [...prev, data]);
        setSuccess('Nouveau personnel ajouté (temps réel)');
      }
    },
    onPersonnelUpdated: (data) => {
      if (activeTab === 'personnel') {
        setPersonnelConfigs(prev => prev.map(p => p.id === data.id ? data : p));
        setSuccess('Personnel modifié (temps réel)');
      }
    },
    onPersonnelDeleted: (data) => {
      if (activeTab === 'personnel') {
        setPersonnelConfigs(prev => prev.filter(p => p.id !== data.id));
        setSuccess('Personnel supprimé (temps réel)');
      }
    },
    onVehiculeCreated: (data) => {
      if (activeTab === 'vehicules') {
        setVehiculeConfigs(prev => [...prev, data]);
        setSuccess('Nouveau véhicule ajouté (temps réel)');
      }
    },
    onVehiculeUpdated: (data) => {
      if (activeTab === 'vehicules') {
        setVehiculeConfigs(prev => prev.map(v => v.id === data.id ? data : v));
        setSuccess('Véhicule modifié (temps réel)');
      }
    },
    onVehiculeDeleted: (data) => {
      if (activeTab === 'vehicules') {
        setVehiculeConfigs(prev => prev.filter(v => v.id !== data.id));
        setSuccess('Véhicule supprimé (temps réel)');
      }
    },
    onCompetenceCreated: (data) => {
      setCompetences(prev => [...prev, data]);
      if (activeTab === 'competences') {
        setSuccess('Nouvelle compétence ajoutée (temps réel)');
      }
    },
    onCompetenceUpdated: (data) => {
      setCompetences(prev => prev.map(c => c.id === data.id ? data : c));
      if (activeTab === 'competences') {
        setSuccess('Compétence modifiée (temps réel)');
      }
    },
    onCompetenceDeleted: (data) => {
      setCompetences(prev => prev.filter(c => c.id !== data.id));
      if (activeTab === 'competences') {
        setSuccess('Compétence supprimée (temps réel)');
      }
    },
  });

  // Gestion des personnels
  const handleAddPersonnel = () => {
    setEditingPersonnel(null);
    const preService = personnelFilterService === 'all' ? 0 : personnelFilterService;
    setPersonnelForm({
      grade: '',
      prix: 0,
      id_competences: [],
      id_service: preService,
      ordre: 0,
    });
    setShowPersonnelModal(true);
  };

  const handleEditPersonnel = (personnel: PersonnelConfig) => {
    setEditingPersonnel(personnel);
    setPersonnelForm({
      grade: personnel.grade,
      prix: personnel.prix,
      id_competences: personnel.id_competences || [],
      id_service: personnel.id_service,
      ordre: personnel.ordre ?? 0,
    });
    setShowPersonnelModal(true);
  };

  const handleSavePersonnel = async () => {
    try {
      setError(null);
      let result;
      if (editingPersonnel?.id) {
        result = await configService.updatePersonnelConfig(editingPersonnel.id, personnelForm);
      } else {
        result = await configService.createPersonnelConfig(personnelForm);
      }
      if (result.success) {
        setSuccess(editingPersonnel ? 'Personnel modifié avec succès' : 'Personnel ajouté avec succès');
        setShowPersonnelModal(false);
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeletePersonnel = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce personnel ?')) return;
    try {
      setError(null);
      const result = await configService.deletePersonnelConfig(id);
      if (result.success) {
        setSuccess('Personnel supprimé avec succès');
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la suppression');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  // Gestion des véhicules
  const handleAddVehicule = () => {
    setEditingVehicule(null);
    setVehiculeForm({
      nom: '',
      niveaux: 1,
      prix: 0,
      specialiste: false,
      id_competences_armement: [],
      id_grades_priorite: [],
      id_grades_armement: [],
      kilometrage: 0,
      type: '',
      fonction_depart: '',
      capacite_personnels_min: 0,
      capacite_personnels_max: 0,
      capable_remorque: false,
      capable_lot: false,
      capable_cellule: false,
      capacite_eau: 0,
      capacite_emulseur: 0,
      id_service: 0,
      primary_armament_function_id: null,
      secondary_armament_function_ids: [],
      id_fonctions_armement: [],
      capacite_victimes_ur: 0,
      capacite_victimes_ua: 0,
      capacite_victimes_impliquees: 0,
      capacite_hostiles: 0,
      capacite_vehicules_accidentes: 0,
      capacite_animaux: 0,
      capable_escorte_medicale: false,
      capable_escorte_fdo: false,
    });
    setShowVehiculeModal(true);
  };

  const handleEditVehicule = (vehicule: VehiculeConfig) => {
    setEditingVehicule(vehicule);
    const serviceIdRaw =
      vehicule.id_service ?? (vehicule as VehiculeConfig & { service_id?: number }).service_id;
    const idServiceNum = parseInt(String(serviceIdRaw ?? 0), 10);
    const id_service =
      Number.isInteger(idServiceNum) && idServiceNum >= 0 ? idServiceNum : 0;
    setVehiculeForm({
      nom: vehicule.nom,
      niveaux: vehicule.niveaux,
      prix: vehicule.prix,
      specialiste: vehicule.specialiste,
      kilometrage: vehicule.kilometrage,
      type: vehicule.type,
      fonction_depart: vehicule.fonction_depart,
      capacite_personnels_min: vehicule.capacite_personnels_min,
      capacite_personnels_max: vehicule.capacite_personnels_max,
      capable_remorque: vehicule.capable_remorque,
      capable_lot: vehicule.capable_lot,
      capable_cellule: vehicule.capable_cellule,
      capacite_eau: vehicule.capacite_eau,
      capacite_emulseur: vehicule.capacite_emulseur,
      id_service,
      primary_armament_function_id: vehicule.primary_armament_function_id ?? null,
      secondary_armament_function_ids:
        vehicule.secondary_armament_function_ids?.length
          ? [...vehicule.secondary_armament_function_ids]
          : vehicule.id_fonctions_armement?.length
            ? [...vehicule.id_fonctions_armement]
            : [],
      id_competences_armement: (() => {
        let ids = vehicule.id_competences_armement?.length
          ? [...vehicule.id_competences_armement]
          : [];
        const legacy =
          vehicule.armement_competence_id ?? vehicule.id_categorie_specialite;
        const legacyNum = legacy != null ? parseInt(String(legacy), 10) : NaN;
        if (ids.length === 0 && Number.isInteger(legacyNum) && legacyNum > 0) {
          ids = [legacyNum];
        }
        return ids;
      })(),
      id_grades_priorite: (vehicule.id_grades_priorite?.length ? vehicule.id_grades_priorite :
        vehicule.id_grades_armement?.length ? vehicule.id_grades_armement : []) as number[],
      id_grades_armement: (vehicule.id_grades_priorite?.length ? vehicule.id_grades_priorite :
        vehicule.id_grades_armement?.length ? vehicule.id_grades_armement : []) as number[],
      id_fonctions_armement:
        vehicule.secondary_armament_function_ids?.length
          ? [...vehicule.secondary_armament_function_ids]
          : vehicule.id_fonctions_armement?.length
            ? [...vehicule.id_fonctions_armement]
            : [],
      capacite_victimes_ur: vehicule.capacite_victimes_ur ?? 0,
      capacite_victimes_ua: vehicule.capacite_victimes_ua ?? 0,
      capacite_victimes_impliquees: vehicule.capacite_victimes_impliquees ?? 0,
      capacite_hostiles: vehicule.capacite_hostiles ?? 0,
      capacite_vehicules_accidentes: vehicule.capacite_vehicules_accidentes ?? 0,
      capacite_animaux: vehicule.capacite_animaux ?? 0,
      capable_escorte_medicale: Boolean(vehicule.capable_escorte_medicale),
      capable_escorte_fdo: Boolean(vehicule.capable_escorte_fdo),
    });
    setShowVehiculeModal(true);
  };

  const handleSaveVehicule = async () => {
    try {
      setError(null);
      const prio = vehiculeForm.id_grades_priorite?.length
        ? vehiculeForm.id_grades_priorite
        : vehiculeForm.id_grades_armement ?? [];
      const vehiculePayload: Omit<VehiculeConfig, 'id'> = {
        ...vehiculeForm,
        id_grades_priorite: prio,
        id_grades_armement: prio,
        secondary_armament_function_ids: Array.from(
          new Set((vehiculeForm.secondary_armament_function_ids || []).filter((id) => id !== vehiculeForm.primary_armament_function_id))
        ),
        id_fonctions_armement: Array.from(
          new Set((vehiculeForm.secondary_armament_function_ids || []).filter((id) => id !== vehiculeForm.primary_armament_function_id))
        ),
      };
      if (!vehiculePayload.id_service || vehiculePayload.id_service < 1) {
        setError('Sélectionnez un service pour ce véhicule.');
        return;
      }
      if (!vehiculePayload.primary_armament_function_id || vehiculePayload.primary_armament_function_id < 1) {
        setError('Sélectionnez une fonction d’armement principale.');
        return;
      }
      let result;
      if (editingVehicule?.id) {
        result = await configService.updateVehiculeConfig(editingVehicule.id, vehiculePayload);
      } else {
        result = await configService.createVehiculeConfig(vehiculePayload);
      }
      if (result.success) {
        setSuccess(editingVehicule ? 'Véhicule modifié avec succès' : 'Véhicule ajouté avec succès');
        setShowVehiculeModal(false);
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteVehicule = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) return;
    try {
      setError(null);
      const result = await configService.deleteVehiculeConfig(id);
      if (result.success) {
        setSuccess('Véhicule supprimé avec succès');
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la suppression');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };


  // Gestion des compétences
  const handleAddCompetence = () => {
    setEditingCompetence(null);
    setCompetenceForm({
      nom: '',
      is_specialite: false,
      id_service: null,
      description: '',
      ordre: undefined,
    });
    setShowCompetenceModal(true);
  };

  const handleEditCompetence = (competence: Competence) => {
    setEditingCompetence(competence);
    setCompetenceForm({
      nom: competence.nom,
      is_specialite: competence.is_specialite,
      id_service: competence.id_service,
      description: competence.description || '',
      ordre: competence.ordre,
    });
    setShowCompetenceModal(true);
  };

  const handleSaveCompetence = async () => {
    try {
      setError(null);
      let result;
      if (editingCompetence?.id) {
        result = await configService.updateCompetence(editingCompetence.id, competenceForm);
      } else {
        result = await configService.createCompetence(competenceForm);
      }
      if (result.success) {
        setSuccess(editingCompetence ? 'Compétence modifiée avec succès' : 'Compétence ajoutée avec succès');
        setShowCompetenceModal(false);
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteCompetence = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette compétence ?')) return;
    try {
      setError(null);
      const result = await configService.deleteCompetence(id);
      if (result.success) {
        setSuccess('Compétence supprimée avec succès');
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la suppression');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };


  // Gestion des fonctions d'armement
  const handleAddFonctionArmement = () => {
    setEditingFonctionArmement(null);
    setFonctionArmementForm({
      nom: '',
      description: '',
      id_competences: [],
    });
    setShowFonctionArmementModal(true);
  };

  const handleEditFonctionArmement = (fonctionArmement: FonctionArmement) => {
    setEditingFonctionArmement(fonctionArmement);
    // Gérer la compatibilité : si id_competences est un tableau de nombres, convertir en tableau d'objets
    let formattedCompetences: CompetenceFonctionArmement[] = [];
    if (fonctionArmement.id_competences) {
      if (Array.isArray(fonctionArmement.id_competences)) {
        if (fonctionArmement.id_competences.length > 0) {
          const firstItem = fonctionArmement.id_competences[0];
          if (typeof firstItem === 'number') {
            // Ancien format : [1, 2, 3] -> convertir en [{id: 1, ordre: 1}, {id: 2, ordre: 2}, ...]
            formattedCompetences = (fonctionArmement.id_competences as unknown as number[]).map((id, index) => ({ id, ordre: index + 1, optionnel: false, ref: false }));
          } else if (typeof firstItem === 'object' && 'id' in firstItem && 'ordre' in firstItem) {
            // Nouveau format : [{id: 1, ordre: 1, optionnel: false, ref: false}, {id: 2, ordre: 2, optionnel: true, ref: false}, ...]
            formattedCompetences = fonctionArmement.id_competences.map(c => ({ ...c, optionnel: c.optionnel || false, ref: c.ref || false }));
          }
        }
      }
    }
    setFonctionArmementForm({
      nom: fonctionArmement.nom,
      description: fonctionArmement.description || '',
      id_competences: formattedCompetences,
    });
    setShowFonctionArmementModal(true);
  };

  const handleSaveFonctionArmement = async () => {
    try {
      setError(null);
      let result;
      if (editingFonctionArmement?.id) {
        result = await configService.updateFonctionArmement(editingFonctionArmement.id, fonctionArmementForm);
      } else {
        result = await configService.createFonctionArmement(fonctionArmementForm);
      }
      if (result.success) {
        setSuccess(editingFonctionArmement ? 'Fonction d\'armement modifiée avec succès' : 'Fonction d\'armement ajoutée avec succès');
        setShowFonctionArmementModal(false);
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteFonctionArmement = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette fonction d\'armement ?')) return;
    try {
      setError(null);
      const result = await configService.deleteFonctionArmement(id);
      if (result.success) {
        setSuccess('Fonction d\'armement supprimée avec succès');
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la suppression');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  // Catégories de mission CTA
  const resetMissionCategoryForm = () => {
    setEditingMissionCategoryId(null);
    setNewCategoryName('');
  };

  const handleEditMissionCategory = (c: (typeof categoriesMission)[number]) => {
    setError(null);
    setEditingMissionCategoryId(c.id);
    setNewCategoryName(c.nom || '');
  };

  const handleCancelEditMissionCategory = () => {
    setError(null);
    resetMissionCategoryForm();
  };

  const handleSaveMissionCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      setError('Veuillez saisir un nom de catégorie.');
      return;
    }
    setError(null);
    setCreatingCategory(true);
    const idBeingSaved = editingMissionCategoryId;
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const url = idBeingSaved
        ? `${apiUrl}/api/cta/categories-mission/${idBeingSaved}`
        : `${apiUrl}/api/cta/categories-mission`;
      const res = await fetch(url, {
        method: idBeingSaved ? 'PUT' : 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ nom: name }),
      });
      const data = await res.json();
      if (data.success && data.data) {
        if (idBeingSaved) {
          setCategoriesMission((prev) => prev.map((x) => (x.id === idBeingSaved ? data.data : x)));
        } else {
          setCategoriesMission((prev) => [...prev, data.data]);
        }
        resetMissionCategoryForm();
        setSuccess(idBeingSaved ? 'Catégorie mise à jour.' : 'Catégorie ajoutée.');
      } else {
        setError(data.error || (idBeingSaved ? 'Erreur lors de la mise à jour.' : 'Erreur lors de l\'ajout.'));
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'enregistrement.');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleDeleteMissionCategory = async (id: number) => {
    if (!window.confirm(t('categoriesMission.deleteConfirm'))) return;
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const headers: HeadersInit = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const res = await fetch(`${apiUrl}/api/cta/categories-mission/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        setCategoriesMission((prev) => prev.filter((c) => c.id !== id));
        if (editingMissionCategoryId === id) resetMissionCategoryForm();
        setSuccess('Catégorie supprimée.');
      } else {
        setError(data.error || 'Erreur lors de la suppression.');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression.');
    }
  };

  const handleCreateCategorieSpe = async () => {
    const nom = newCategorieSpeNom.trim();
    if (!nom) {
      setError('Veuillez saisir un nom de catégorie de spécialité.');
      return;
    }
    if (!newCategorieSpeServiceId || newCategorieSpeServiceId < 1) {
      setError('Veuillez sélectionner un service.');
      return;
    }
    setError(null);
    setCreatingCategorieSpe(true);
    try {
      const apiUrl = getApiBaseUrl();
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const res = await fetch(`${apiUrl}/api/config/categories-specialite`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ nom, id_service: newCategorieSpeServiceId, ordre: 0, actif: 1 }),
      });
      const data = await res.json();
      if (data.success && data.data) {
        setCategoriesSpecialite((prev) => [...prev, data.data]);
        setNewCategorieSpeNom('');
        setNewCategorieSpeServiceId(services[0]?.id ?? 0);
        setSuccess('Catégorie de spécialité ajoutée.');
      } else {
        setError(data.error || 'Erreur lors de l\'ajout.');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'ajout.');
    } finally {
      setCreatingCategorieSpe(false);
    }
  };

  // Bâtiments (config types pour le menu construction carte)
  const handleEditBuilding = (b: BuildingTypeConfig) => {
    setEditingBuildingId(b.id);
    setBuildingForm({
      code: b.code,
      nom: b.nom,
      icon: b.icon || '',
      couleur: b.couleur || 'text-gray-400',
      ordre: b.ordre,
      actif: b.actif,
      service_id: b.service_id ?? services[0]?.id ?? 1,
      prix: typeof b.prix === 'number' ? b.prix : Number(b.prix) || 0,
    });
  };

  const handleCancelEditBuilding = () => {
    setEditingBuildingId(null);
    setBuildingForm({
      code: '',
      nom: '',
      icon: '',
      couleur: 'text-gray-400',
      ordre: 0,
      actif: 1,
      service_id: services[0]?.id ?? 1,
      prix: 0,
    });
  };

  const handleSaveBuilding = async () => {
    const code = buildingForm.code.trim();
    const nom = buildingForm.nom.trim();
    if (!code || !nom) {
      setError('Code et nom sont requis.');
      return;
    }
    setError(null);
    setSavingBuilding(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const body = {
        code: code.toLowerCase().replace(/\s+/g, '_'),
        nom,
        icon: buildingForm.icon.trim() || null,
        couleur: buildingForm.couleur.trim() || null,
        ordre: buildingForm.ordre,
        actif: buildingForm.actif ? 1 : 0,
        service_id: buildingForm.service_id,
        prix: buildingForm.prix,
      };
      if (editingBuildingId) {
        const res = await fetch(`${apiUrl}/api/buildings/config/${editingBuildingId}`, {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.success && data.data) {
          setBuildingTypesConfig((prev) => prev.map((x) => (x.id === editingBuildingId ? data.data : x)));
          setSuccess('Type de bâtiment mis à jour.');
          handleCancelEditBuilding();
        } else {
          setError(data.error || 'Erreur lors de la mise à jour.');
        }
      } else {
        const res = await fetch(`${apiUrl}/api/buildings/config`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.success && data.data) {
          setBuildingTypesConfig((prev) => [...prev, data.data]);
          setSuccess('Type de bâtiment ajouté.');
          setBuildingForm({
            code: '',
            nom: '',
            icon: '',
            couleur: 'text-gray-400',
            ordre: 0,
            actif: 1,
            service_id: services[0]?.id ?? 1,
            prix: 0,
          });
        } else {
          setError(data.error || 'Erreur lors de l\'ajout.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur.');
    } finally {
      setSavingBuilding(false);
    }
  };

  const handleDeleteBuilding = async (id: number) => {
    if (!window.confirm('Supprimer ce type de bâtiment ? Les bâtiments déjà placés sur la carte garderont leur type.')) return;
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${apiUrl}/api/buildings/config/${id}`, { method: 'DELETE', headers, credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setBuildingTypesConfig((prev) => prev.filter((x) => x.id !== id));
        setSuccess('Type de bâtiment supprimé.');
        if (editingBuildingId === id) handleCancelEditBuilding();
      } else {
        setError(data.error || 'Erreur lors de la suppression.');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur.');
    }
  };

  // Partenaires (page d'accueil)
  const handleEditPartner = (p: PartenaireConfig) => {
    setEditingPartnerId(p.id);
    setPartnerForm({
      code: p.code,
      nom: p.nom,
      description: p.description || '',
      discord_link: p.discord_link,
      color: (PARTENAIRE_COLORS.includes(p.color as any) ? p.color : 'blue') as typeof PARTENAIRE_COLORS[number],
      icon: p.icon || '',
      ordre: p.ordre,
      actif: p.actif,
    });
  };

  const handleCancelEditPartner = () => {
    setEditingPartnerId(null);
    setPartnerForm({
      code: '',
      nom: '',
      description: '',
      discord_link: '',
      color: 'blue',
      icon: '',
      ordre: 0,
      actif: 1,
    });
  };

  const handleSavePartner = async () => {
    const nom = partnerForm.nom.trim();
    const discord_link = partnerForm.discord_link.trim();
    if (!nom || !discord_link) {
      setError('Nom et lien Discord sont requis.');
      return;
    }
    setError(null);
    setSavingPartner(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const body = {
        code: partnerForm.code.trim() || nom.toLowerCase().replace(/\s+/g, '-'),
        nom,
        description: partnerForm.description.trim() || null,
        discord_link,
        color: partnerForm.color,
        icon: partnerForm.icon.trim() || null,
        ordre: partnerForm.ordre,
        actif: partnerForm.actif ? 1 : 0,
      };
      if (editingPartnerId) {
        const res = await fetch(`${apiUrl}/api/config/partenaires/${editingPartnerId}`, {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.success && data.data) {
          setPartenairesConfig((prev) => prev.map((x) => (x.id === editingPartnerId ? data.data : x)));
          setSuccess('Partenaire mis à jour.');
          handleCancelEditPartner();
        } else {
          setError(data.error || 'Erreur lors de la mise à jour.');
        }
      } else {
        const res = await fetch(`${apiUrl}/api/config/partenaires`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.success && data.data) {
          setPartenairesConfig((prev) => [...prev, data.data]);
          setSuccess('Partenaire ajouté.');
          handleCancelEditPartner();
        } else {
          setError(data.error || 'Erreur lors de l\'ajout.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur.');
    } finally {
      setSavingPartner(false);
    }
  };

  const handleDeletePartner = async (id: number) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${apiUrl}/api/config/partenaires/${id}`, { method: 'DELETE', headers, credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setPartenairesConfig((prev) => prev.filter((x) => x.id !== id));
        setSuccess('Partenaire supprimé.');
        if (editingPartnerId === id) handleCancelEditPartner();
      } else {
        setError(data.error || 'Erreur lors de la suppression.');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur.');
    }
  };

  // Fermer les messages après 5 secondes
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  if (!currentUser) {
    return null;
  }

  if (!hasAccess) {
    return (
      <div className={embedded ? '' : 'min-h-screen relative overflow-hidden'}>
        {!embedded && <BackgroundImage />}
        {!embedded && <ParticlesBackground />}
        {!embedded && <Navbar />}
        <div className={embedded ? '' : 'relative z-10 pt-24 pb-16'}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="p-6">
              <div className="text-center text-white">
                <h2 className="text-2xl font-bold mb-4">{t('accessDenied.title')}</h2>
                <p className="text-gray-400">{t('accessDenied.message')}</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen relative overflow-hidden'}>
      {!embedded && <BackgroundImage />}
      {!embedded && <ParticlesBackground />}
      {!embedded && <Navbar />}
      <div className={embedded ? '' : 'relative z-10 pt-24 pb-16'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header avec bouton retour */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold text-white mb-2 text-gradient">
                {t('title')}
              </h1>
              <p className="text-gray-300">
                {t('subtitle')}
              </p>
            </div>
            {!embedded && (
              <button
                onClick={() => navigate('/panel')}
                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>{t('backToPanel')}</span>
              </button>
            )}
          </div>

          {/* Messages d'erreur/succès */}
          {error && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-200">
              {success}
            </div>
          )}

          {/* Contenu principal */}
          <Card className="p-6">
            {/* Onglets */}
            <div className="flex border-b border-gray-700 mb-6">
              <button
                onClick={() => setActiveTab('personnel')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'personnel'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('tabs.personnel')}
              </button>
              <button
                onClick={() => setActiveTab('vehicules')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'vehicules'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('tabs.vehicules')}
              </button>
              <button
                onClick={() => setActiveTab('competences')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'competences'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('tabs.competences')}
              </button>
              <button
                onClick={() => setActiveTab('fonctions-armement')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'fonctions-armement'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('tabs.fonctionsArmement')}
              </button>
              <button
                onClick={() => setActiveTab('batiments')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'batiments'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('tabs.batiments')}
              </button>
              <button
                onClick={() => setActiveTab('partenaires')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'partenaires'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('tabs.partenaires')}
              </button>
              <button
                onClick={() => setActiveTab('categories-mission')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'categories-mission'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('tabs.categoriesMission')}
              </button>
              <button
                onClick={() => setActiveTab('categories-spe')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'categories-spe'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('tabs.categoriesSpe')}
              </button>
            </div>

            {/* Contenu des onglets */}
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="mt-6">
                {activeTab === 'personnel' && (
                  <PersonnelTab
                    personnelConfigs={personnelConfigs}
                    searchPersonnel={searchPersonnel}
                    onSearchChange={setSearchPersonnel}
                    filterService={personnelFilterService}
                    onFilterServiceChange={setPersonnelFilterService}
                    onAdd={handleAddPersonnel}
                    onEdit={handleEditPersonnel}
                    onDelete={handleDeletePersonnel}
                    services={services}
                    competences={competences}
                  />
                )}

                {activeTab === 'vehicules' && (
                  <VehiculesTab
                    vehiculeConfigs={vehiculeConfigs}
                    searchVehicule={searchVehicule}
                    onSearchChange={setSearchVehicule}
                    onAdd={handleAddVehicule}
                    onEdit={handleEditVehicule}
                    onDelete={handleDeleteVehicule}
                    services={services}
                    personnelConfigs={personnelConfigs}
                    competences={competences}
                  />
                )}

                {activeTab === 'competences' && (
                  <CompetencesTab
                    competences={competences}
                    searchCompetence={searchCompetence}
                    onSearchChange={setSearchCompetence}
                    filterType={filterType}
                    onFilterTypeChange={setFilterType}
                    filterService={filterService}
                    onFilterServiceChange={setFilterService}
                    onAdd={handleAddCompetence}
                    onEdit={handleEditCompetence}
                    onDelete={handleDeleteCompetence}
                    services={services}
                  />
                )}

                {activeTab === 'fonctions-armement' && (
                  <FonctionsArmementTab
                    fonctionsArmement={fonctionsArmement}
                    searchFonctionsArmement={searchFonctionsArmement}
                    onSearchChange={setSearchFonctionsArmement}
                    onAdd={handleAddFonctionArmement}
                    onEdit={handleEditFonctionArmement}
                    onDelete={handleDeleteFonctionArmement}
                    competences={competences}
                  />
                )}

                {activeTab === 'batiments' && (
                  <div className="space-y-6">
                    <p className="text-sm text-gray-400">
                      {t('batiments.description')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('batiments.codeLabel')}</label>
                        <input
                          type="text"
                          value={buildingForm.code}
                          onChange={(e) => setBuildingForm((f) => ({ ...f, code: e.target.value }))}
                          placeholder={t('batiments.codePlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                          disabled={!!editingBuildingId}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('batiments.nomLabel')}</label>
                        <input
                          type="text"
                          value={buildingForm.nom}
                          onChange={(e) => setBuildingForm((f) => ({ ...f, nom: e.target.value }))}
                          placeholder={t('batiments.nomPlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('batiments.serviceLabel')}</label>
                        <select
                          value={buildingForm.service_id}
                          onChange={(e) =>
                            setBuildingForm((f) => ({ ...f, service_id: parseInt(e.target.value, 10) || 1 }))
                          }
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                        >
                          {services.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.nom}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">{t('batiments.serviceHint')}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('batiments.prixLabel')}</label>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={Number.isFinite(buildingForm.prix) ? buildingForm.prix : 0}
                          onChange={(e) =>
                            setBuildingForm((f) => ({
                              ...f,
                              prix: Math.max(0, parseFloat(e.target.value) || 0),
                            }))
                          }
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <BuildingAppearanceFields
                        icon={buildingForm.icon}
                        couleur={buildingForm.couleur}
                        onIconChange={(icon) => setBuildingForm((f) => ({ ...f, icon }))}
                        onCouleurChange={(couleur) => setBuildingForm((f) => ({ ...f, couleur }))}
                        labels={{
                          iconLabel: t('batiments.iconLabel'),
                          iconPlaceholder: t('batiments.iconPlaceholder'),
                          iconPickHint: t('batiments.iconPickHint'),
                          couleurLabel: t('batiments.couleurLabel'),
                          couleurPlaceholder: t('batiments.couleurPlaceholder'),
                          colorPickHint: t('batiments.colorPickHint'),
                          customTailwind: t('batiments.customTailwind'),
                        }}
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('batiments.ordreLabel')}</label>
                        <input
                          type="number"
                          min={0}
                          value={buildingForm.ordre}
                          onChange={(e) => setBuildingForm((f) => ({ ...f, ordre: parseInt(e.target.value, 10) || 0 }))}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="building-actif"
                          checked={!!buildingForm.actif}
                          onChange={(e) => setBuildingForm((f) => ({ ...f, actif: e.target.checked ? 1 : 0 }))}
                          className="rounded border-gray-600 bg-gray-800 text-primary-500"
                        />
                        <label htmlFor="building-actif" className="text-sm text-gray-300">{t('batiments.actifLabel')}</label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSaveBuilding}
                        disabled={savingBuilding}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium disabled:opacity-60"
                      >
                        {savingBuilding ? t('batiments.saving') : editingBuildingId ? t('batiments.update') : t('batiments.add')}
                      </button>
                      {editingBuildingId && (
                        <button
                          type="button"
                          onClick={handleCancelEditBuilding}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium"
                        >
                          {t('batiments.cancel')}
                        </button>
                      )}
                    </div>
                    <div className="border-t border-gray-700 pt-4">
                      <h2 className="text-lg font-semibold text-white mb-3">{t('batiments.listTitle')}</h2>
                      {loading ? (
                        <p className="text-sm text-gray-500">{t('batiments.loading')}</p>
                      ) : buildingTypesConfig.length === 0 ? (
                        <p className="text-sm text-gray-500">{t('batiments.empty')}</p>
                      ) : (
                        <ul className="divide-y divide-gray-800">
                          {buildingTypesConfig.map((b) => (
                            <li key={b.id} className="py-2 flex items-center justify-between gap-4">
                              <span className="text-2xl">{b.icon || '🏢'}</span>
                              <span
                                className={`flex-1 ${
                                  b.actif ? b.couleur || 'text-gray-200' : 'text-gray-500'
                                }`}
                              >
                                {b.nom} <span className="text-gray-500">({b.code})</span>
                                {!b.actif && <span className="ml-2 text-xs text-amber-400">{t('batiments.inactif')}</span>}
                              </span>
                              <span className="text-xs text-gray-500">
                                {t('batiments.ordre')}: {b.ordre} · {t('batiments.prixShort')}{' '}
                                {typeof b.prix === 'number' ? b.prix.toFixed(2) : Number(b.prix || 0).toFixed(2)} €
                              </span>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditBuilding(b)}
                                  className="px-2 py-1 text-sm bg-gray-600 hover:bg-gray-500 text-white rounded"
                                >
                                  {t('batiments.modify')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteBuilding(b.id)}
                                  className="px-2 py-1 text-sm bg-red-600/80 hover:bg-red-600 text-white rounded"
                                >
                                  {t('batiments.delete')}
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'partenaires' && (
                  <div className="space-y-6">
                    <p className="text-sm text-gray-400">
                      {t('partenaires.description')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.codeLabel')}</label>
                        <input
                          type="text"
                          value={partnerForm.code}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, code: e.target.value }))}
                          placeholder={t('partenaires.codePlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                          disabled={!!editingPartnerId}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.nomLabel')}</label>
                        <input
                          type="text"
                          value={partnerForm.nom}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, nom: e.target.value }))}
                          placeholder={t('partenaires.nomPlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.descriptionLabel')}</label>
                        <input
                          type="text"
                          value={partnerForm.description}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, description: e.target.value }))}
                          placeholder={t('partenaires.descriptionPlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.discordLabel')}</label>
                        <input
                          type="url"
                          value={partnerForm.discord_link}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, discord_link: e.target.value }))}
                          placeholder={t('partenaires.discordPlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.couleurLabel')}</label>
                        <select
                          value={partnerForm.color}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, color: e.target.value as typeof PARTENAIRE_COLORS[number] }))}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                        >
                          {PARTENAIRE_COLORS.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-400">{t('partenaires.discordLogoNote')}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.ordreLabel')}</label>
                        <input
                          type="number"
                          min={0}
                          value={partnerForm.ordre}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, ordre: parseInt(e.target.value, 10) || 0 }))}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="partner-actif"
                          checked={!!partnerForm.actif}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, actif: e.target.checked ? 1 : 0 }))}
                          className="rounded border-gray-600 bg-gray-800 text-primary-500"
                        />
                        <label htmlFor="partner-actif" className="text-sm text-gray-300">{t('partenaires.actifLabel')}</label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSavePartner}
                        disabled={savingPartner}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium disabled:opacity-60"
                      >
                        {savingPartner ? t('partenaires.saving') : editingPartnerId ? t('partenaires.update') : t('partenaires.add')}
                      </button>
                      {editingPartnerId && (
                        <button
                          type="button"
                          onClick={handleCancelEditPartner}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium"
                        >
                          {t('partenaires.cancel')}
                        </button>
                      )}
                    </div>
                    <div className="border-t border-gray-700 pt-4">
                      <h2 className="text-lg font-semibold text-white mb-3">{t('partenaires.listTitle')}</h2>
                      {loading ? (
                        <p className="text-sm text-gray-500">{t('partenaires.loading')}</p>
                      ) : partenairesConfig.length === 0 ? (
                        <p className="text-sm text-gray-500">{t('partenaires.empty')}</p>
                      ) : (
                        <ul className="divide-y divide-gray-800">
                          {partenairesConfig.map((p) => (
                            <li key={p.id} className="py-2 flex items-center justify-between gap-4">
                              <span className={`flex-1 ${p.actif ? 'text-gray-200' : 'text-gray-500'}`}>
                                {p.nom} <span className="text-gray-500">({p.code})</span>
                                {!p.actif && <span className="ml-2 text-xs text-amber-400">{t('partenaires.inactif')}</span>}
                              </span>
                              <span className="text-xs text-gray-500">{t('partenaires.ordre')}: {p.ordre}</span>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditPartner(p)}
                                  className="px-2 py-1 text-sm bg-gray-600 hover:bg-gray-500 text-white rounded"
                                >
                                  {t('partenaires.modify')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeletePartner(p.id)}
                                  className="px-2 py-1 text-sm bg-red-600/80 hover:bg-red-600 text-white rounded"
                                >
                                  {t('partenaires.delete')}
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'categories-mission' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg font-semibold text-white mb-3">
                        {editingMissionCategoryId ? t('categoriesMission.editTitle') : t('categoriesMission.addTitle')}
                      </h2>
                      <p className="text-sm text-gray-400 mb-2">{t('categoriesMission.addDescription')}</p>
                      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          placeholder={t('categoriesMission.placeholder')}
                          className="flex-1 min-w-[200px] px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleSaveMissionCategory}
                            disabled={creatingCategory}
                            className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-60"
                          >
                            {creatingCategory
                              ? t('categoriesMission.saving')
                              : editingMissionCategoryId
                                ? t('categoriesMission.update')
                                : t('categoriesMission.add')}
                          </button>
                          {editingMissionCategoryId && (
                            <button
                              type="button"
                              onClick={handleCancelEditMissionCategory}
                              disabled={creatingCategory}
                              className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-60"
                            >
                              {t('categoriesMission.cancel')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-gray-700 pt-4">
                      <h2 className="text-lg font-semibold text-white mb-3">{t('categoriesMission.listTitle')}</h2>
                      {loading ? (
                        <p className="text-sm text-gray-500">{t('categoriesMission.loading')}</p>
                      ) : categoriesMission.length === 0 ? (
                        <p className="text-sm text-gray-500">{t('categoriesMission.empty')}</p>
                      ) : (
                        <ul className="divide-y divide-gray-800">
                          {[...categoriesMission].sort((a, b) => a.nom.localeCompare(b.nom, 'fr')).map((c) => (
                            <li
                              key={c.id}
                              className={`py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${editingMissionCategoryId === c.id ? 'rounded-lg bg-primary-900/20 px-3 -mx-1' : ''}`}
                            >
                              <div className="flex flex-wrap justify-between items-center gap-2 text-gray-200 flex-1 min-w-0">
                                <span className="font-medium">{c.nom}</span>
                                <span className="text-xs text-gray-500 shrink-0">ID: {c.id}</span>
                              </div>
                              <div className="flex gap-2 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handleEditMissionCategory(c)}
                                  disabled={!!creatingCategory}
                                  className="px-2 py-1 text-sm bg-gray-600 hover:bg-gray-500 text-white rounded disabled:opacity-50"
                                >
                                  {t('categoriesMission.modify')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteMissionCategory(c.id)}
                                  disabled={!!creatingCategory}
                                  className="px-2 py-1 text-sm bg-red-600/80 hover:bg-red-600 text-white rounded disabled:opacity-50"
                                >
                                  {t('categoriesMission.delete')}
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'categories-spe' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg font-semibold text-white mb-3">{t('categoriesSpe.addTitle')}</h2>
                      <p className="text-sm text-gray-400 mb-2">{t('categoriesSpe.addDescription')}</p>
                      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                        <input
                          type="text"
                          value={newCategorieSpeNom}
                          onChange={(e) => setNewCategorieSpeNom(e.target.value)}
                          placeholder={t('categoriesSpe.placeholder')}
                          className="flex-1 min-w-[200px] px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <select
                          value={newCategorieSpeServiceId || ''}
                          onChange={(e) => setNewCategorieSpeServiceId(Number(e.target.value))}
                          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white min-w-[180px]"
                        >
                          <option value={0}>— {t('categoriesSpe.selectService')} —</option>
                          {services.map((s) => (
                            <option key={s.id} value={s.id}>{s.nom}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleCreateCategorieSpe}
                          disabled={creatingCategorieSpe}
                          className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-60"
                        >
                          {creatingCategorieSpe ? t('categoriesSpe.adding') : t('categoriesSpe.add')}
                        </button>
                      </div>
                    </div>
                    <div className="border-t border-gray-700 pt-4">
                      <h2 className="text-lg font-semibold text-white mb-3">{t('categoriesSpe.listTitle')}</h2>
                      {loading ? (
                        <p className="text-sm text-gray-500">{t('categoriesSpe.loading')}</p>
                      ) : categoriesSpecialite.length === 0 ? (
                        <p className="text-sm text-gray-500">{t('categoriesSpe.empty')}</p>
                      ) : (
                        <ul className="divide-y divide-gray-800">
                          {[...categoriesSpecialite].sort((a, b) => a.nom.localeCompare(b.nom, 'fr')).map((c) => (
                            <li key={c.id} className="py-2 flex justify-between text-gray-200">
                              <span>{c.nom}{c.service_nom ? ` (${c.service_nom})` : ''}</span>
                              <span className="text-xs text-gray-500">ID: {c.id}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Modale Personnel */}
      {showPersonnelModal && (
        <PersonnelModal
          editingPersonnel={editingPersonnel}
          personnelForm={personnelForm}
          setPersonnelForm={setPersonnelForm}
          services={services}
          competences={competences}
          onClose={() => setShowPersonnelModal(false)}
          onSave={handleSavePersonnel}
        />
      )}

      {/* Modale Véhicule */}
      {showVehiculeModal && (
        <VehiculeModal
          editingVehicule={editingVehicule}
          vehiculeForm={vehiculeForm}
          setVehiculeForm={setVehiculeForm}
          services={services}
          personnelConfigs={personnelConfigs}
          competences={competences}
          fonctionsArmement={fonctionsArmement}
          onClose={() => setShowVehiculeModal(false)}
          onSave={handleSaveVehicule}
        />
      )}

      {/* Modale Compétence */}
      {showCompetenceModal && (
        <CompetenceModal
          editingCompetence={editingCompetence}
          competenceForm={competenceForm}
          setCompetenceForm={setCompetenceForm}
          services={services}
          onClose={() => setShowCompetenceModal(false)}
          onSave={handleSaveCompetence}
        />
      )}

      {/* Modale Fonction d'armement */}
      {showFonctionArmementModal && (
        <FonctionArmementModal
          editingFonctionArmement={editingFonctionArmement}
          fonctionArmementForm={fonctionArmementForm}
          setFonctionArmementForm={setFonctionArmementForm}
          competences={competences}
          services={services}
          onClose={() => setShowFonctionArmementModal(false)}
          onSave={handleSaveFonctionArmement}
        />
      )}
    </div>
  );
};

export default SystemConfiguration;
